import React from 'react';
import { useAuthenticationState } from '../hooks/useAuthenticationState';
import { ApiTestService } from '../services/apiTestService';
import { JWTDecoder } from '../types/jwt.types';

export const Dashboard: React.FC = () => {
  const {
    isAuthenticated,
    isLoading,
    tokens,
    userInfo,
    authMethod,
    error,
    refreshTokens,
    logout,
    clearError,
  } = useAuthenticationState();

  const handleTestProtectedEndpoint = () => {
    ApiTestService.testAndAlert(tokens?.accessToken);
  };

  const handleRefreshTokens = async () => {
    try {
      await refreshTokens();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const copyToken = async () => {
    if (tokens?.accessToken) {
      try {
        await navigator.clipboard.writeText(tokens.accessToken);
        alert('Access token copied to clipboard!');
      } catch (e) {
        alert('Failed to copy token to clipboard');
      }
    }
  };

  const getTokenInfo = () => {
    if (!tokens?.accessToken) return null;
    
    const decoded = JWTDecoder.decode(tokens.accessToken);
    if (!decoded) return null;

    return {
      expiryDate: JWTDecoder.getExpiryDate(tokens.accessToken),
      isExpired: JWTDecoder.isExpired(tokens.accessToken),
      scopes: decoded.payload.scp?.split(' ') || [],
    };
  };

  const formatExpirationTime = () => {
    const tokenInfo = getTokenInfo();
    if (!tokenInfo?.expiryDate) return 'Unknown';
    
    const now = new Date();
    const expiry = tokenInfo.expiryDate;
    
    if (tokenInfo.isExpired) {
      return `Expired ${Math.floor((now.getTime() - expiry.getTime()) / 1000 / 60)} minutes ago`;
    }
    
    const minutesUntilExpiry = Math.floor((expiry.getTime() - now.getTime()) / 1000 / 60);
    return `${minutesUntilExpiry} minutes remaining`;
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading authentication state...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard-container">
        <div className="error-content">
          <h2>Authentication Required</h2>
          <p>You are not authenticated. Please go back and log in.</p>
          <button onClick={() => window.location.href = '/'} className="back-button">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const tokenInfo = getTokenInfo();

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={clearError} className="close-error">×</button>
          </div>
        )}

        <div className="dashboard-header">
          <h1>Authentication Dashboard</h1>
          <p>Welcome, {userInfo?.name || userInfo?.email || 'User'}!</p>
        </div>

        <div className="dashboard-grid">
          {/* User Information */}
          <div className="card">
            <h3>User Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{userInfo?.name || 'Not available'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{userInfo?.email || 'Not available'}</span>
              </div>
              <div className="info-item">
                <label>User ID:</label>
                <span>{userInfo?.id || 'Not available'}</span>
              </div>
              <div className="info-item">
                <label>Tenant ID:</label>
                <span>{userInfo?.tenantId || 'Not available'}</span>
              </div>
              <div className="info-item">
                <label>Auth Method:</label>
                <span className="auth-method">{authMethod || 'Unknown'}</span>
              </div>
            </div>
          </div>

          {/* Token Information */}
          <div className="card">
            <h3>Token Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Token Type:</label>
                <span>{tokens?.tokenType || 'Bearer'}</span>
              </div>
              <div className="info-item">
                <label>Expires:</label>
                <span className={tokenInfo?.isExpired ? 'expired' : 'valid'}>
                  {formatExpirationTime()}
                </span>
              </div>
              <div className="info-item">
                <label>Has Refresh Token:</label>
                <span>{tokens?.refreshToken ? 'Yes' : 'No'}</span>
              </div>
              <div className="info-item">
                <label>Scopes:</label>
                <span className="scopes">
                  {tokenInfo?.scopes?.join(', ') || 'None'}
                </span>
              </div>
            </div>
            <div className="token-actions">
              <button onClick={copyToken} className="copy-token-button">
                Copy Access Token
              </button>
              {tokenInfo?.isExpired && (
                <button onClick={handleRefreshTokens} className="refresh-button">
                  Refresh Token
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="dashboard-actions">
          <button onClick={handleTestProtectedEndpoint} className="test-button">
            Test Protected Endpoint
          </button>
          <button onClick={handleLogout} className="logout-button">
            Sign Out
          </button>
        </div>

        <div className="dashboard-info">
          <h3>Authentication Complete</h3>
          <p>
            You are now authenticated with Azure AD. Your application can use the access token 
            to make authorized requests to protected APIs.
          </p>
        </div>
      </div>

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 0.5rem 0.25rem;
        }

        .dashboard-content {
          max-width: 1000px;
          margin: 0 auto;
        }

        .loading-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 50vh;
          text-align: center;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e3e3e3;
          border-top: 4px solid #0078d4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-content {
          text-align: center;
          background: white;
          padding: 3rem;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .error-banner {
          background: #ffebee;
          border: 1px solid #f8bbd9;
          border-radius: 6px;
          padding: 1rem;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #d32f2f;
        }

        .close-error {
          background: none;
          border: none;
          font-size: 0.9rem;
          cursor: pointer;
          color: #d32f2f;
        }

        .dashboard-header {
          text-align: center;
          margin-bottom: 0.75rem;
        }

        .dashboard-header h1 {
          color: #333;
          margin-bottom: 0.25rem;
          font-size: 1.1rem;
        }

        .dashboard-header p {
          color: #666;
          font-size: 0.85rem;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .card {
          background: white;
          padding: 0.75rem;
          border-radius: 6px;
          box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
        }

        .card h3 {
          color: #333;
          margin-bottom: 0.5rem;
          font-size: 1rem;
          border-bottom: 1px solid #0078d4;
          padding-bottom: 0.25rem;
        }

        .info-grid {
          display: grid;
          gap: 0.5rem;
        }

        .info-item {
          display: grid;
          grid-template-columns: 80px 1fr;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
        }

        .info-item label {
          font-weight: 600;
          color: #555;
        }

        .info-item span {
          color: #333;
          word-break: break-all;
        }

        .auth-method {
          text-transform: capitalize;
          background: #e3f2fd;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .expired {
          color: #d32f2f;
          font-weight: 600;
        }

        .valid {
          color: #4caf50;
          font-weight: 600;
        }

        .scopes {
          font-size: 0.8rem;
          font-family: monospace;
        }

        .token-actions {
          margin-top: 0.75rem;
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .copy-token-button, .refresh-button {
          background: #0078d4;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.8rem;
          transition: background 0.2s;
        }

        .copy-token-button:hover, .refresh-button:hover {
          background: #106ebe;
        }

        .dashboard-actions {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .test-button, .logout-button, .back-button {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .test-button {
          background: linear-gradient(45deg, #4caf50, #45a049);
          color: white;
        }

        .test-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(76, 175, 80, 0.3);
        }

        .logout-button {
          background: linear-gradient(45deg, #f44336, #d32f2f);
          color: white;
        }

        .logout-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(244, 67, 54, 0.3);
        }

        .back-button {
          background: linear-gradient(45deg, #0078d4, #106ebe);
          color: white;
        }

        .back-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 120, 212, 0.3);
        }

        .dashboard-info {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .dashboard-info h3 {
          color: #333;
          margin-bottom: 1rem;
        }

        .dashboard-info p {
          color: #666;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          
          .info-item {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
          
          .dashboard-actions {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};